import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import {
  ActionsContact, Bloc, CarteLigne, Chiffres, Fiche, Kv, LienOrdinateur, Liste, Pill, Plaque, Puces, Recherche, Statut,
  dateFr, messageErreur, quand, useFicheOuverte, useToast,
} from './MobileUI';
import { TYPE_ANOMALIE } from '../shared/AnomaliePiece';
import { afficherImmat } from '../../utils/immat';
import { texteTheme } from '../../utils/theme';
import './PiecesMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Pièces atelier — version téléphone (2026-10-24).
//
// Le cas d'usage : le magasinier, le réceptionnaire ou le chef d'atelier
// cherche un OR (n° d'OR, plaque, client ou RÉFÉRENCE PIÈCE) et regarde si
// les pièces sont là, où elles en sont chez le transitaire, ce qui est entré
// en magasin. LECTURE SEULE : aucune écriture depuis le téléphone (ni
// anomalie, ni résolution) ; la saisie reste sur ordinateur.
//
// Routes (backend/src/routes/carnet-commande.js) :
//   - liste : `GET /carnet-commande/mobile?q=&file=&limit=50` — une ligne
//     légère par OR, files et compteurs calculés côté serveur, cloisonnés à la
//     concession (super-admin : toutes ; téléopérateur : sa plaque). La liste
//     ordinateur charge tout le carnet (21 624 OR) : intenable en 4G ;
//   - fiche : `GET /carnet-commande/mobile/:numOr` — pièces une par une
//     (commandé / reçu, référence remplacée, stock magasin de la concession de
//     l'OR, jalon transitaire, anomalie), bons de livraison SANS montant,
//     anomalies résolues, RDV et téléphone du dossier Planning.
// Files : « Tout » par défaut (OR des 90 derniers jours ; avec une recherche,
// tout le carnet), À venir (RDV J-1..J+14 pas tout reçu), Arrivées 7 j,
// En route (transitaire), Anomalies.
// Gestes sans écriture : Appeler le client, copier un n° de commande.
// ═══════════════════════════════════════════════════════════════════════════

const STATUTS = {
  en_commande: { label: 'En commande', couleur: 'light-dark(#ef4444, color-mix(in srgb, #ef4444 60%, white))' },
  recu_partiellement: { label: 'Partiel', couleur: 'light-dark(#f59e0b, color-mix(in srgb, #f59e0b 60%, white))' },
  livre: { label: 'Reçu', couleur: 'light-dark(#10b981, color-mix(in srgb, #10b981 60%, white))' },
};
const statutDe = (v) => STATUTS[v] || { label: v || '—', couleur: 'var(--text-muted)' };
const STATUT_PIECE = { livre: 'reçue', livre_partiellement: 'partielle', en_commande: 'en commande' };

const FILES = [
  { cle: 'tout', libelle: 'Tout' },
  { cle: 'a_venir', libelle: 'À venir' },
  { cle: 'arrivees', libelle: 'Arrivées 7 j' },
  { cle: 'en_route', libelle: 'En route' },
  { cle: 'anomalies', libelle: 'Anomalies', alerte: true },
];
const VIDES = {
  tout: 'Aucun OR avec des pièces depuis 90 jours.',
  a_venir: 'Aucun RDV des 14 prochains jours n’attend de pièce.',
  arrivees: 'Aucune arrivée de pièces ces 7 derniers jours.',
  en_route: 'Aucune pièce en route chez le transitaire.',
  anomalies: 'Aucune anomalie active.',
};
const MODES = { AER: '✈', MAR: '🚢', MIX: '✈🚢' };
const LIMITE = 50;

// ── Dates (jour local de l'appareil) ─────────────────────────────────────────
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const jourIso = (decalage = 0) => { const d = new Date(); d.setDate(d.getDate() + decalage); return isoLocal(d); };
const jj = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}` : '');
function jourCourtSemaine(iso) {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR', { weekday: 'short' })} ${jj(iso)}`;
}
function libelleJour(iso) {
  if (!iso) return 'Sans RDV';
  if (iso === jourIso(0)) return 'Aujourd’hui';
  if (iso === jourIso(1)) return `Demain · ${jourCourtSemaine(iso)}`;
  if (iso === jourIso(-1)) return `Hier · ${jourCourtSemaine(iso)}`;
  const s = jourCourtSemaine(iso);
  return s.charAt(0).toUpperCase() + s.slice(1);
}
const rdvTexte = (rdv) => (rdv ? `${jourCourtSemaine(rdv.date)}${rdv.heure ? ` · ${rdv.heure}` : ''}` : '');

function transportTexte(t) {
  if (!t) return '';
  return `${MODES[t.mode] || '📦'} ${t.mot}${t.date ? ` ${jj(t.date)}` : ''}${t.litige ? ' · litige' : ''}`;
}

async function copier(texte) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(texte); return true; }
  } catch (e) { /* repli ci-dessous */ }
  try {
    const zone = document.createElement('textarea');
    zone.value = texte;
    zone.setAttribute('readonly', '');
    zone.style.position = 'fixed';
    zone.style.opacity = '0';
    document.body.appendChild(zone);
    zone.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(zone);
    return ok;
  } catch (e) {
    return false;
  }
}

export default function PiecesMobile({ user, onVersionOrdinateur }) {
  const voitPlusieurs = user?.role === 'super-admin' || user?.role === 'teleoperateur';
  const [file, setFile] = useState('tout');
  const [recherche, setRecherche] = useState('');
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [toast, afficher] = useToast();
  const numeroRequete = useRef(0);

  const q = recherche.trim().length >= 2 ? recherche.trim() : '';

  const charger = useCallback(async (silencieux) => {
    const n = ++numeroRequete.current;
    if (!silencieux) setChargement(true);
    try {
      const r = await api.get('/carnet-commande/mobile', { params: { file, q: q || undefined, limit: LIMITE } });
      if (n !== numeroRequete.current) return; // une réponse plus récente est attendue
      setDonnees(r.data);
      setErreur('');
    } catch (e) {
      if (n !== numeroRequete.current) return;
      if (!silencieux) setErreur(messageErreur(e, 'Les pièces n’ont pas pu être chargées.'));
    } finally {
      if (n === numeroRequete.current) setChargement(false);
    }
  }, [file, q]);
  useEffect(() => { charger(false); }, [charger]);

  // Les données Datacar changent toutes les 15 min : on recharge au retour sur
  // l'onglet, pas en continu (batterie, 4G).
  useEffect(() => {
    const tic = () => { if (document.visibilityState === 'visible') charger(true); };
    document.addEventListener('visibilitychange', tic);
    return () => document.removeEventListener('visibilitychange', tic);
  }, [charger]);

  const elements = donnees?.elements || [];
  const cle = useCallback((o) => o.num_or, []);
  const fiche = useFicheOuverte(elements, cle);
  const comptes = donnees?.comptes || {};
  const chiffres = donnees?.chiffres;
  const auj = jourIso(0);
  const demain = jourIso(1);

  const puces = FILES.map((f) => ({ ...f, compte: comptes[f.cle] }));

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-pieces-q" valeur={recherche} delai={400}
          onChange={(v) => {
            // Une nouvelle recherche part de « Tout » : chercher un OR dans
            // « À venir » le cacherait s'il n'a pas de RDV.
            if (v.trim().length >= 2 && !q) setFile('tout');
            setRecherche(v);
          }}
          placeholder="OR, plaque, client, référence pièce" />
      </div>
      <Puces label="Files" valeur={file} onChange={setFile} options={puces} />
      {chiffres && elements.length > 0 && (
        <Chiffres items={[
          { libelle: 'en commande', valeur: chiffres.en_commande },
          { libelle: 'partiels', valeur: chiffres.partiels },
          { libelle: 'anomalies', valeur: chiffres.anomalies, alerte: chiffres.anomalies > 0 },
        ]} />
      )}
      {recherche.trim().length === 1 && <p className="mb-aide mb-aide--centre">Tapez au moins 2 caractères.</p>}
      {q && <p className="mb-aide mb-aide--centre">Recherche dans tout le carnet{file !== 'tout' ? ', file « ' + FILES.find((f) => f.cle === file).libelle + ' »' : ''}.</p>}
      {file === 'en_route' && donnees && !donnees.sifa_disponible && (
        <p className="mb-aide mb-aide--centre">Suivi du transitaire indisponible pour le moment.</p>
      )}

      <Liste
        elements={elements}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={q ? 'Aucun OR ne correspond.' : VIDES[file]}
        groupe={file === 'a_venir' ? (o) => libelleJour(o.rdv?.date) : null}
        onOuvrir={fiche.ouvrir}
        carte={(o) => {
          const s = statutDe(o.statut);
          const proche = !!o.rdv && o.statut !== 'livre' && o.rdv.date >= auj && o.rdv.date <= demain;
          const age = o.rdv
            ? (file === 'a_venir' ? (o.rdv.heure || jj(o.rdv.date)) : `RDV ${jj(o.rdv.date)}`)
            : '';
          return {
            couleur: s.couleur,
            contenu: (
              <>
                <CarteLigne avant={<Plaque immat={afficherImmat(o.immat)} />} titre={o.nom_client || 'Client inconnu'}
                  age={age} retard={proche} />
                <span className="mb-carte-texte">{[`OR ${o.num_or}`, o.vehicule].filter(Boolean).join(' · ')}</span>
                <span className="mb-carte-meta">
                  <Statut libelle={s.label} couleur={s.couleur} />
                  <Pill ton={o.n_pieces && o.n_recues === o.n_pieces ? 'ok' : undefined}>{o.n_recues}/{o.n_pieces} reçue{o.n_pieces > 1 ? 's' : ''}</Pill>
                  {o.transport && <Pill ton={o.transport.litige || o.transport.retard ? 'ko' : undefined}>{transportTexte(o.transport)}</Pill>}
                  {o.nb_anomalies > 0 && <Pill ton="ko">⚠ {o.nb_anomalies}</Pill>}
                  {o.au_parking && <Pill>🅿️</Pill>}
                  {voitPlusieurs && o.concession_nom && <Pill>{o.concession_nom}</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      {donnees && donnees.total > elements.length && (
        <p className="mb-aide mb-aide--centre">
          {elements.length} premiers sur {donnees.tronque ? `plus de ${donnees.total}` : donnees.total}{q ? ' : précisez la recherche.' : '. Cherchez un OR, une plaque ou une référence.'}
        </p>
      )}
      <LienOrdinateur onClick={onVersionOrdinateur}>Anomalies, montants, toutes les colonnes : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FichePieces
          key={fiche.ouverte.num_or}
          resume={fiche.ouverte}
          position={fiche.index}
          total={elements.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FichePieces({ resume, position, total, onRetour, onPrecedent, onSuivant, afficher, onVersionOrdinateur }) {
  const [detail, setDetail] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    const controle = new AbortController();
    api.get(`/carnet-commande/mobile/${encodeURIComponent(resume.num_or)}`, { signal: controle.signal })
      .then((r) => setDetail(r.data))
      .catch((e) => { if (!controle.signal.aborted) setErreur(messageErreur(e, 'Le détail de l’OR n’a pas pu être chargé.')); });
    return () => controle.abort();
  }, [resume.num_or]);

  const d = detail || resume;
  const s = statutDe(d.statut);
  const lignes = detail?.lignes || [];
  const nRecues = detail ? lignes.filter((l) => l.statut === 'livre').length : resume.n_recues;
  const nPieces = detail ? lignes.length : resume.n_pieces;
  const tel = detail?.client_tel || detail?.client_tel2 || '';
  const enTransit = lignes.filter((l) => l.sifa);
  const nbBl = detail?.bons_livraison?.length || 0;
  const resolues = detail?.anomalies_resolues || [];

  const copierCommande = async (cde) => {
    afficher(await copier(cde) ? `N° de commande ${cde} copié` : 'Copie impossible sur cet appareil');
  };

  const rdvAlerte = !!d.rdv && d.statut !== 'livre' && d.rdv.date <= jourIso(1) && d.rdv.date >= jourIso(-1);

  return (
    <Fiche
      titre={`OR ${resume.num_or}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={detail ? <ActionsContact tel={tel} /> : null}
    >
      <div className="mb-entete">
        <div className="mb-entete-haut"><Plaque immat={afficherImmat(d.immat)} />{d.vehicule || ''}</div>
        <div>
          <div className="mb-entete-titre">{d.nom_client || 'Client inconnu'}</div>
          <div className="mb-entete-sous">
            {[d.receptionnaire && `Réceptionnaire : ${d.receptionnaire}`, d.concession_nom].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="mb-pc-statut">
          <Statut libelle={s.label} couleur={s.couleur} />
          <span>{nRecues}/{nPieces} pièce{nPieces > 1 ? 's' : ''} reçue{nPieces > 1 ? 's' : ''}</span>
        </div>
        <div className={`mb-pc-rdv${rdvAlerte ? ' is-alerte' : ''}`}>
          <span aria-hidden="true">📅</span>
          {d.rdv ? (
            <div>
              <b>RDV {rdvTexte(d.rdv)}</b>
              <small>
                {d.rdv.statut_atelier
                  ? <span style={{ color: texteTheme(d.rdv.couleur) || undefined }}>{d.rdv.statut_atelier}</span>
                  : 'Dossier Planning'}
                {rdvAlerte ? ' · pièces manquantes à l’approche du RDV' : ''}
              </small>
            </div>
          ) : (
            <div><b>Pas de RDV au planning</b><small>Date de service : {dateFr(d.date_service) || '—'}</small></div>
          )}
        </div>
        {detail && !tel && <p className="mb-aide">Aucun téléphone au dossier Planning.</p>}
      </div>

      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      {!detail && !erreur && <p className="mb-aide mb-aide--centre">Chargement du détail…</p>}

      {detail && (
        <>
          <Bloc titre="Pièces" compte={lignes.length} resume={`${nRecues}/${lignes.length} reçues`} ouvert>
            {lignes.length === 0 && <p className="mb-aide">Aucune pièce sur cet OR.</p>}
            <div className="mb-pc-pieces">
              {lignes.map((l, i) => <LignePiece key={`${l.reference}-${i}`} l={l} onCopier={copierCommande} />)}
            </div>
          </Bloc>

          {enTransit.length > 0 && (
            <Bloc titre="Transport" resume="transitaire">
              <div className="mb-pc-pieces">
                {enTransit.map((l, i) => (
                  <div key={`${l.cde}-${i}`} className="mb-pc-transport">
                    <b>{l.description || l.reference}</b>
                    <Kv lignes={[
                      ['Mode', `${MODES[l.sifa.transport] || '📦'} ${l.sifa.transport === 'MAR' ? 'Maritime' : l.sifa.transport === 'AER' ? 'Aérien' : 'Transport'}${l.sifa.compagnie ? ` · ${l.sifa.compagnie}` : ''}`],
                      l.sifa.no_conteneur ? ['Conteneur', l.sifa.no_conteneur] : l.sifa.ref_expedition && ['LTA', l.sifa.ref_expedition],
                      ['Départ', l.sifa.date_etd ? dateFr(l.sifa.date_etd) : '—'],
                      ['Arrivée prévue', l.sifa.date_eta ? dateFr(l.sifa.date_eta) : '—'],
                      l.sifa.date_rta && ['Arrivé le', `${dateFr(l.sifa.date_rta)} (constaté)`],
                      l.sifa.date_dedouanement && ['Dédouané le', dateFr(l.sifa.date_dedouanement)],
                      l.sifa.litige && ['Litige', <Pill ton="ko">Litige ouvert</Pill>],
                      l.sifa.arrivee_en_retard && ['Retard', <Pill ton="attente">Arrivée en retard</Pill>],
                      l.cde && ['Commande', <span className="mb-pc-cde">n° {l.cde}{l.date_envoi ? ` · envoyée le ${dateFr(l.date_envoi)}` : ''}<button type="button" className="mb-mini" onClick={() => copierCommande(l.cde)}>Copier</button></span>],
                    ]} />
                  </div>
                ))}
              </div>
              <p className="mb-aide">La réception au magasin, elle, est le bon de livraison Datacar.</p>
            </Bloc>
          )}

          <Bloc titre="Bons de livraison" compte={nbBl} resume={nbBl ? '' : 'aucun'}>
            {nbBl === 0 && <p className="mb-aide">Rien n’est encore entré en magasin pour cet OR.</p>}
            {detail.bons_livraison.map((b, i) => (
              <div key={`${b.id_cde}-${i}`} className="mb-pc-bl">
                <div className="mb-pc-bl-tete">
                  <b>Commande {b.id_cde || '—'}</b>
                  {b.receptionnaire && <small>{b.receptionnaire}</small>}
                </div>
                {b.lignes.map((x, k) => (
                  <div key={k} className="mb-pc-bl-ligne">
                    <span className="mb-pc-ref">{x.reference || '—'}</span>
                    <span>{x.designation || ''}</span>
                    <span className="mb-pc-qte">{x.qte_livree ?? 0}/{x.qte_commandee ?? 0}</span>
                    {x.commentaire && <small>{x.commentaire}</small>}
                  </div>
                ))}
              </div>
            ))}
            {nbBl > 0 && <p className="mb-aide">Montants : version ordinateur.</p>}
          </Bloc>

          <Bloc titre="Historique des anomalies" compte={resolues.length} resume={resolues.length ? '' : 'aucune résolue'}>
            {resolues.length === 0 && <p className="mb-aide">Aucune anomalie résolue sur cet OR.</p>}
            {resolues.map((a) => (
              <div key={a.id} className="mb-commentaire">
                <b>{(TYPE_ANOMALIE[a.type] || TYPE_ANOMALIE.autre).label}</b> · <span className="mb-pc-ref">{a.reference}</span>
                {a.commentaire ? ` — ${a.commentaire}` : ''}
                <small>Signalée {a.par ? `par ${a.par} ` : ''}le {quand(a.le)}</small>
                <small>Résolue {a.resolue_par ? `par ${a.resolue_par} ` : ''}le {quand(a.resolue_le)}{a.resolution ? ` : ${a.resolution}` : ''}</small>
              </div>
            ))}
          </Bloc>
        </>
      )}

      <p className="mb-aide mb-aide--centre">Signaler ou résoudre une anomalie, facturation, montants : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />
    </Fiche>
  );
}

function LignePiece({ l, onCopier }) {
  const ton = l.statut === 'livre' ? 'ok' : l.statut === 'livre_partiellement' ? 'attente' : 'ko';
  const jalon = l.sifa?.jalon;
  const t = l.anomalie ? (TYPE_ANOMALIE[l.anomalie.type] || TYPE_ANOMALIE.autre) : null;
  return (
    <div className="mb-pc-piece">
      <div className="mb-pc-piece-haut">
        <b>{l.description || l.reference || 'Pièce'}</b>
        <Pill ton={ton}>{l.qte_livree}/{l.qte_commandee} {STATUT_PIECE[l.statut] || ''}</Pill>
      </div>
      <div className="mb-pc-piece-ligne">
        {l.ref_rempl
          ? <><s className="mb-pc-ref">{l.reference}</s> → <span className="mb-pc-ref">{l.ref_rempl}</span></>
          : <span className="mb-pc-ref">{l.reference || '—'}</span>}
        {l.stock != null && <Pill ton={l.stock > 0 ? 'ok' : undefined}>{l.stock > 0 ? `${l.stock} en stock magasin` : 'pas en stock'}</Pill>}
      </div>
      {jalon && (
        <div className="mb-pc-piece-ligne">
          <span>{MODES[l.sifa.transport] || '📦'} {jalon.mot} {jalon.date ? jj(jalon.date) : ''}</span>
          {l.sifa.litige && <Pill ton="ko">litige</Pill>}
        </div>
      )}
      {(l.fournisseur || l.cde) && (
        <div className="mb-pc-piece-ligne mb-pc-discret">
          <span>{[l.fournisseur, l.cde && `cde ${l.cde}`, l.date_envoi && `envoyée le ${dateFr(l.date_envoi)}`].filter(Boolean).join(' · ')}</span>
          {l.cde && <button type="button" className="mb-mini" onClick={() => onCopier(l.cde)} aria-label={`Copier le n° de commande ${l.cde}`}>Copier</button>}
        </div>
      )}
      {t && (
        <div className="mb-pc-anomalie">
          <Pill ton="ko">⚠ {t.label}</Pill>
          <span>{l.anomalie.commentaire || ''}<small>{l.anomalie.par ? `${l.anomalie.par} · ` : ''}{quand(l.anomalie.le)}</small></span>
        </div>
      )}
    </div>
  );
}
